import { useEffect } from 'react';

/**
 * useAppBadge
 *
 * Paints the OS-level red dot on the home-screen icon while a session
 * is pending today. Cleared once the user has trained (last_session_at
 * is today). Chrome desktop, Chrome Android (PWA installed), and Edge
 * support navigator.setAppBadge. iOS Safari does not yet, so we guard
 * with a feature check and silently no-op.
 *
 * Counts are not meaningful here (either today is due or it is not),
 * so we pass 1 rather than a session count. setAppBadge with no arg
 * also paints a generic dot; we pass 1 explicitly so the API is
 * consistent across browsers that optionally render the number.
 */
export default function useAppBadge({ lastSessionAt, pending = true }) {
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.setAppBadge) return undefined;

    const shouldBadge = pending && !isSameLocalDay(lastSessionAt, new Date());

    if (shouldBadge) {
      try { navigator.setAppBadge(1); } catch { /* permission denied or OS refused */ }
    } else {
      try { navigator.clearAppBadge?.(); } catch { /* cleanup ok */ }
    }

    // Always clear on unmount so a user who logs out or closes the app
    // mid-session doesn't leave a stale dot forever.
    return () => {
      try { navigator.clearAppBadge?.(); } catch { /* cleanup */ }
    };
  }, [lastSessionAt, pending]);
}

function isSameLocalDay(iso, now) {
  if (!iso) return false;
  try {
    const d = new Date(iso);
    return d.getFullYear() === now.getFullYear()
      && d.getMonth() === now.getMonth()
      && d.getDate() === now.getDate();
  } catch { return false; }
}
